export type SearchTourPrepareAction =
  | 'open-actions'
  | 'close-actions'
  | 'open-column-menu'
  | 'close-column-menu'
  | 'open-row-detail'
  | 'aida-open-rename'
  | 'advanced-search-show-examples'
  | 'advanced-search-run-search-list'
  | 'advanced-search-back-to-input'
  | 'enrichment-show-examples'
  | 'enrichment-open-modal'
  | 'enrichment-run-enrichment'
  | 'enrichment-submit-modal'
  | 'enrichment-back-to-input'
  | 'enrichment-close-modal'
  | 'api-authorize'
  | 'api-load-examples'
  | 'api-try-it-out'
  | 'open-table-controls'
  | 'expand-sidebar-columns'
  | 'expand-sidebar-my-score'
  | 'expand-sidebar-search-items'
  | 'sticky-open-palette'
  | 'sticky-create-note'
  | 'sticky-ensure-open-note'
  | 'sticky-minimize-note'
  | 'sticky-prepare-restore';

export type SearchTourStep = {
  id: string;
  title: string;
  body: string;
  /** Primary target to spotlight */
  selector: string;
  /** Fallback when primary is missing (e.g. auth-gated) */
  fallbackSelector?: string;
  prepare?: SearchTourPrepareAction[];
  note?: string;
  /**
   * `corner` pins the tip to the bottom-right (for steps without a concrete control,
   * or when the target is optional / often missing).
   */
  tipPlacement?: 'auto' | 'corner';
};

export const SEARCH_TOUR_STEPS: SearchTourStep[] = [
  {
    id: 'download',
    title: 'Download results',
    body: 'From Actions you can export the current table (TSV, Excel, etc.). Downloads are available when you search a specific entity — not in Search All.',
    selector: '[data-demo-tour="actions-download"]',
    fallbackSelector: '[data-demo-tour="actions-button"]',
    prepare: ['open-actions'],
  },
  {
    id: 'enrichment',
    title: 'Select rows for Enrichment',
    body: 'Use Select for enrichment to pick rows from the table and send that set into the Enrichment analysis workflow.',
    selector: '[data-demo-tour="actions-enrichment"]',
    fallbackSelector: '[data-demo-tour="actions-button"]',
    prepare: ['open-actions'],
  },
  {
    id: 'aida-questions',
    title: 'Ask AIDA about a row',
    body: 'Each row has suggested AIDA questions. You can also send the row context for a more open-ended question in the AIDA chat.',
    selector: '[data-demo-tour="row-aida-questions"]',
    prepare: ['close-actions'],
  },
  {
    id: 'row-detail',
    title: 'Open row detail',
    body: 'Open the detail panel to inspect the full field list for that association without leaving the search results.',
    selector: '[data-demo-tour="open-row-detail"]',
  },
  {
    id: 'historical-score',
    title: 'Historical score',
    body: 'Inside the row detail you can switch to Historical score to see how the association score evolved over time.',
    selector: '[data-demo-tour="historical-score-tab"]',
    fallbackSelector: '[data-demo-tour="open-row-detail"]',
    prepare: ['open-row-detail'],
    note: 'If the tab is disabled, your plan may need an upgrade.',
  },
  {
    id: 'reference',
    title: 'Table Reference',
    body: 'Reference explains sources, metrics, and what each column means — useful when the table feels dense.',
    selector: '[data-demo-tour="table-reference"]',
    prepare: ['close-actions', 'close-column-menu'],
  },
  {
    id: 'column-drag',
    title: 'Drag columns',
    body: 'Hover a column header and drag the ⋮⋮ handle to reorder columns directly in the table.',
    selector: '[data-demo-tour="column-drag-handle"]',
  },
  {
    id: 'column-pin',
    title: 'Pin columns',
    body: 'Open column options (⋯) to pin a column left or right. You can pin up to 3 columns so they stay visible while scrolling.',
    selector: '[data-demo-tour="column-pin"]',
    fallbackSelector: '[data-demo-tour="column-options"]',
    prepare: ['open-column-menu'],
  },
  {
    id: 'column-hide',
    title: 'Hide columns from the table',
    body: 'From the same column menu you can hide a column immediately without opening the sidebar.',
    selector: '[data-demo-tour="column-hide"]',
    fallbackSelector: '[data-demo-tour="column-options"]',
    prepare: ['open-column-menu'],
  },
  {
    id: 'sidebar-columns-toggle',
    title: 'Show / hide from Table controls',
    body: 'In Table controls → Columns, toggle visibility for many columns at once (eye icons).',
    selector: '[data-demo-tour="sidebar-column-toggle"]',
    fallbackSelector: '[data-demo-tour="sidebar-columns-toggle"], [data-demo-tour="sidebar-columns"]',
    prepare: ['close-column-menu', 'open-table-controls', 'expand-sidebar-columns'],
  },
  {
    id: 'sidebar-columns-drag',
    title: 'Reorder from Table controls',
    body: 'Drag the handle next to a column name in the sidebar to change order — same result as dragging in the table header.',
    selector: '[data-demo-tour="sidebar-column-drag"]',
    fallbackSelector: '[data-demo-tour="sidebar-columns-toggle"], [data-demo-tour="sidebar-columns"]',
    prepare: ['open-table-controls', 'expand-sidebar-columns'],
  },
  {
    id: 'my-score',
    title: 'Custom My Score',
    body: 'Pick sources, set weights and a score threshold, then Apply weights. My Score personalizes association ranking on GDA/VDA.',
    selector: '[data-demo-tour="sidebar-my-score-content"]',
    fallbackSelector: '[data-demo-tour="sidebar-my-score-toggle"], [data-demo-tour="sidebar-my-score"]',
    prepare: ['open-table-controls', 'expand-sidebar-my-score'],
    note: 'Requires sign-in and a non-academic plan on GDA/VDA tabs.',
  },
  {
    id: 'add-search-item',
    title: 'Add items to the search',
    body: 'In Table controls → Search items, type another gene, disease, variant, or chemical to expand the current query.',
    selector: '[data-demo-tour="add-search-item"]',
    fallbackSelector: '[data-demo-tour="sidebar-search-items-toggle"], [data-demo-tour="sidebar-search-items"]',
    prepare: ['open-table-controls', 'expand-sidebar-search-items'],
  },
  {
    id: 'save-search',
    title: 'Save your search',
    body: 'From Actions → Save search you can store the current view (items, filters, columns) and reopen it later.',
    selector: '[data-demo-tour="actions-save-search"]',
    fallbackSelector: '[data-demo-tour="actions-button"]',
    prepare: ['open-actions'],
    note: 'Save options require an authenticated non-academic / non-trial account.',
  },
];
